import _ from 'lodash';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Button, FormCheck, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import toastr from '../../../toastr';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';
import { IDefaultPart } from '../../typings/DefaultPart';

/* While similar to the ForcePartModal component, this component has enough of its
   own logic specific to default parts to warrant its own implementation.

   Default parts are setup via ATB3.

   All default parts have a PO and description, but in the case of SUBL (sublet labor)
   and SUBP (sublet parts), descrption is supplanted in the modal by "sublet comment",
   which is much longer than a standard description and eventually itemized as a standard
   line item memo once the invoice is processed.

   Secific system pricing escalators exist for sublet labor and sublet parts should the user
   tab or enter past list/sell pricing fields (non sublet default parts use the same system pricing
   escalators as the force price modal).

   The rules of "arity" are as follows (mirroring legacy PIN1):
   3: All pricing fields adjustable, markup/escalators work the same as forced prices
   2: LIST is automatically escalated based on the net price according to system pricing escalators (markup disabled)
   1: LIST and SELL are set to the same as NET (markup disabled)
*/

interface IProps {
  apply: (
    netPrice: number,
    listPrice: number,
    tradePrice: number,
    description: string,
    subletComment: string,
    unpriced: boolean
  ) => void;
  container: Object;
  data: {
    defaultPartNum: string;
    description: string;
    listPrice: string;
    netPrice: string;
    subletComment: string;
    tradePrice: string;
  };
  hide: Function;
  model: Model;
  show: boolean;
  showDefaultNetPrice?: boolean;
  unpricedOptionAvailable?: boolean;
  unpricedOptionChecked?: boolean;
}

@observer
class DefaultPartModal extends Component<IProps, {}> {
  model: Model;

  @observable applyButtonDisabled = true;
  @observable grossProfitPercentage = '';
  @observable showSubletCommentRequired = false;
  @observable unpriced = false;

  // These 3 are inferred on each new mount (see componentWillMount())
  escalatorList = 1;
  escalatorTrade = 1;
  defaultPart: IDefaultPart;
  defaultNetPrice = '';
  subletDefaultPart = false;

  applyButtonRef: HTMLButtonElement;
  descriptionInput: HTMLInputElement;
  listPriceInput: HTMLInputElement;
  markupOverrideInput: HTMLInputElement;
  netPriceInput: HTMLInputElement;
  sellPriceInput: HTMLInputElement;
  subletCommentInput: HTMLInputElement;

  constructor(props: IProps) {
    super(props);
    this.model = props.model;

    // Is this a "faux" default part display? (currently only PO freight from confirmations)
    if (['CONF-FREIGHT', 'PO-FREIGHT'].includes(props.data.defaultPartNum)) {
      // This is contrived, but follows closely to ATB3 "FRT" values
      this.defaultPart = {
        arity: '1',
        defaultNetPrice: '',
        desc: 'FREIGHT',
        partNum: 'FREIGHT', // Ignored
        po: false,
        priceSymbol: '50', // Ignored, QM supplied value will dominate
        reverseSign: false,
        stockClass: '9999', // Ignored, QM supplied value will dominate
      };
    } else if (props.data.defaultPartNum === 'CONF-FUEL') {
      this.defaultPart = {
        arity: '1',
        defaultNetPrice: '',
        desc: 'FUEL',
        partNum: 'FUEL', // Ignored
        po: false,
        priceSymbol: '50', // Ignored, QM supplied value will dominate
        reverseSign: false,
        stockClass: '9999', // Ignored, QM supplied value will dominate
      };

      // Otherwise ATB3 default part
    } else {
      this.defaultPart = this.model.defaultParts.get(props.data.defaultPartNum)!;

      if (props.showDefaultNetPrice) {
        this.defaultNetPrice = this.defaultPart.defaultNetPrice;
      }
    }

    // If the default part description contains the word SUBLET, collect an attached sublet comment
    this.subletDefaultPart = this.defaultPart.desc.toUpperCase().includes('SUBLET');

    let escalatorList, escalatorTrade;
    switch (this.defaultPart.partNum) {
      case 'SUBL':
        escalatorList = this.model.sysParams.escalatorSubletLaborListPrice;
        escalatorTrade = this.model.sysParams.escalatorSubletLaborTradePrice;
        break;
      case 'SUBP':
        escalatorList = this.model.sysParams.escalatorSubletPartsListPrice;
        escalatorTrade = this.model.sysParams.escalatorSubletPartsTradePrice;
        break;
      default:
        escalatorList = this.model.sysParams.escalatorListPrice;
        escalatorTrade = this.model.sysParams.escalatorTradePrice;
        break;
    }

    this.escalatorList = parseFloat(escalatorList) || 1;
    this.escalatorTrade = parseFloat(escalatorTrade) || 1;

    this.unpriced = !!(props.unpricedOptionAvailable && props.unpricedOptionChecked);
  }

  componentDidMount() {
    // Timeout required until RBS fixes their React 16 async slot issues
    setTimeout(() => {
      if (this.subletDefaultPart) {
        // Initial Sublet Part Entry
        if (this.subletCommentInput.value === '') {
          this.subletCommentInput.focus();

          // Thereafter, probably just a price adjustment
        } else {
          this.netPriceInput.focus();
          this.netPriceInput.setSelectionRange(0, this.netPriceInput.value.length);
        }
      } else {
        this.netPriceInput.focus();
        this.netPriceInput.setSelectionRange(0, this.netPriceInput.value.length);
      }
      this.setDefaultPartModalState();
    }, 0);
  }

  apply() {
    let netPrice, listPrice, tradePrice: number;

    if (this.unpriced) {
      netPrice = 0;
      listPrice = 0;
      tradePrice = 0;
    } else {
      // This should only be clickable if net/sell/trade are properly set, check anyway.
      netPrice = parseFloat(this.netPriceInput.value);
      listPrice = parseFloat(this.listPriceInput.value);
      tradePrice = parseFloat(this.sellPriceInput.value); // trade == sell

      // Should never occur as long as the event handlers properly verify data.
      // This is a fast double check in case a handler gets messed up.
      if (isNaN(netPrice) || isNaN(listPrice) || isNaN(tradePrice)) {
        toastr.error('Error', 'Invalid price detected.  Try again.');
        return;
      }
    }

    // Again, should be handled by the event handler, but safeguard against future changes.
    if (this.subletDefaultPart && this.subletCommentInput.value.trim() === '') {
      toastr.error('Error', 'A comment is required on sublets.');
      return;
    }

    // PO input disabled until further notice/need
    // const po = this.poInput.value.trim();

    const po = '';
    const description = this.descriptionInput.value.trim();
    const subletComment = this.subletDefaultPart ? this.subletCommentInput.value.trim() : '';

    // If ATB3 has a setting of "reverse sign" for this default part, enforce it
    // unless the user already entered a negative number (coupons, etc.).
    if (this.defaultPart.reverseSign) {
      netPrice = netPrice > 0 ? -netPrice : netPrice;
      listPrice = listPrice > 0 ? -listPrice : listPrice;
      tradePrice = tradePrice > 0 ? -tradePrice : tradePrice;
    }

    this.props.apply(netPrice, listPrice, tradePrice, description, subletComment, this.unpriced);
  }

  overrideMarkup(advanceField: boolean) {
    const markup = parseInt(this.markupOverrideInput.value.trim(), 10);
    if (!isNaN(markup)) {
      this.markupOverrideInput.value = markup.toString(); // cleanup input if necessary

      // apply markups to list/sell values
      const parsedNetOverride = parseFloat(this.netPriceInput.value.trim());
      if (!isNaN(parsedNetOverride)) {
        this.listPriceInput.value = ((1 + (2 * markup) / 100) * parsedNetOverride).toFixed(2);
        this.sellPriceInput.value = ((1 + markup / 100) * parsedNetOverride).toFixed(2);
      }

      // otherwise junk entered for markup
    } else {
      this.markupOverrideInput.value = '';
    }

    this.setDefaultPartModalState();

    if (advanceField) {
      this.listPriceInput.focus();
      this.listPriceInput.setSelectionRange(0, this.listPriceInput.value.length);
    }
  }

  overrideList(advanceField: boolean) {
    const listOverride = this.listPriceInput.value.trim();

    // User or markup system has already entered a value?  Make sure it's a valid X.XX format and move on to sell field.
    // In the case markup is entered, manually changing this field will dominate the markup calculation.
    if (listOverride !== '') {
      const parsedListOverride = parseFloat(listOverride);
      if (!isNaN(parsedListOverride)) {
        this.listPriceInput.value = parsedListOverride.toFixed(2);
        this.setDefaultPartModalState();
        if (advanceField) {
          this.sellPriceInput.focus();
          this.sellPriceInput.setSelectionRange(0, this.sellPriceInput.value.length);
        }
        return;
      }
      // else user entered junk input ("asdf") and we apply the logic below
    }

    // Here we logically have a blank input (or invalid input)

    // The net field will be either a valid number or blank.  If net is a number,
    // apply the appropriate list escalator as determined on mount.
    const netOverride = parseFloat(this.netPriceInput.value.trim());
    if (!isNaN(netOverride)) {
      this.listPriceInput.value = (this.escalatorList * netOverride).toFixed(2);

      // Go ahead and escalate sell price as well if it's empty
      // This will automatially call setDefaultPartModalState()
      this.overrideSell(false);
    } else {
      // Otherwise clear out any possible junk input (i.e. "asdf")
      this.listPriceInput.value = '';
      this.setDefaultPartModalState();
    }

    // If a markup HAS been specified (and net is set), discard the previous if/else field
    // calculation and apply the net+markup instead (again, only on blank/invalid list input).
    this.overrideMarkup(false);

    if (advanceField) {
      this.sellPriceInput.focus();
      this.sellPriceInput.setSelectionRange(0, this.sellPriceInput.value.length);
    }
  }

  overrideNet(advanceField: boolean) {
    const arity = this.defaultPart.arity;
    const netOverride = this.netPriceInput.value.trim();
    const parsedNetOverride = parseFloat(netOverride);

    if (isNaN(parsedNetOverride)) {
      this.netPriceInput.value = '';
    } else {
      this.netPriceInput.value = parsedNetOverride.toFixed(2);
    }

    // Per legacy PIN1, when arity = 1, NET/LIST/TRADE are all the same
    if (arity === '1') {
      this.listPriceInput.value = this.sellPriceInput.value = this.netPriceInput.value;
    }

    if (arity === '2') {
      if (this.netPriceInput.value === '') {
        this.listPriceInput.value = '';
      } else {
        this.listPriceInput.value = (parsedNetOverride * this.escalatorList).toFixed(2);
      }
    }

    // Apply any existing markup, if present
    this.overrideMarkup(false);

    this.setDefaultPartModalState();

    if (advanceField) {
      switch (arity) {
        case '1':
          if (this.netPriceInput.value !== '') {
            // Give Apply button time to enable
            setTimeout(() => (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus(), 0);
          }
          break;
        case '2':
          this.sellPriceInput.focus();
          this.sellPriceInput.setSelectionRange(0, this.sellPriceInput.value.length);
          break;
        default:
          this.markupOverrideInput.focus();
          this.markupOverrideInput.setSelectionRange(0, this.markupOverrideInput.value.length);
          break;
      }
    }
  }

  overrideSell(advanceField: boolean) {
    const sellOverride = this.sellPriceInput.value.trim();

    // User or markup system has already entered a value?  Make sure it's a valid X.XX format and move on to apply button.
    // In the case markup is entered, manually changing this field will dominate the markup calculation.
    if (sellOverride !== '') {
      const parsedSellOverride = parseFloat(sellOverride);
      if (!isNaN(parsedSellOverride)) {
        this.sellPriceInput.value = parsedSellOverride.toFixed(2);
        this.setDefaultPartModalState();
        if (advanceField) {
          // Give Apply button time to enable
          setTimeout(() => {
            (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus();
          }, 0);
        }
        return;
      }
      // else user entered junk input ("asdf") and we apply the logic below
    }

    // Here we logically have a blank input (or invalid input)

    // The net field will be either a valid number or blank.  If net is a number,
    // apply the appropriate list escalator as determined on mount.
    const netOverride = parseFloat(this.netPriceInput.value.trim());
    if (!isNaN(netOverride)) {
      this.sellPriceInput.value = (this.escalatorTrade * netOverride).toFixed(2);
    } else {
      // Otherwise clear out any possible junk input (i.e. "asdf")
      this.sellPriceInput.value = '';
    }

    this.setDefaultPartModalState();

    // If a markup HAS been specified (and net is set), discard the previous if/else field
    // calculation and apply the net+markup instead (again, only on blank/invalid sell input).
    this.overrideMarkup(false);

    if (advanceField) {
      // Give Apply button time to enable
      setTimeout(() => (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus(), 0);
    }
  }

  // Always strip leading "-".  Reverse sign is applied automatically.
  positiveString(value: string) {
    return value.startsWith('-') ? value.slice(1) : value;
  }

  setSubletComment(advanceField: boolean) {
    if (this.subletCommentInput.value.trim() === '') {
      this.showSubletCommentRequired = true;
      this.subletCommentInput.focus();
    } else {
      this.showSubletCommentRequired = false;

      if (advanceField) {
        this.netPriceInput.focus();
        this.netPriceInput.setSelectionRange(0, this.netPriceInput.value.length);
      }
    }

    this.setDefaultPartModalState();
  }

  // Determines if the "Apply" button should be active (all req fields present)
  setDefaultPartModalState() {
    if (
      ((this.netPriceInput.value !== '' && this.listPriceInput.value !== '' && this.sellPriceInput.value !== '') ||
        this.unpriced) &&
      (this.subletDefaultPart ? this.subletCommentInput.value !== '' : true)
    ) {
      this.applyButtonDisabled = false;

      // Calculate gross profit %
      // This should only run if net/sell/trade are properly set, check anyway.
      const netPriceForce = parseFloat(this.netPriceInput.value);
      const listPriceForce = parseFloat(this.listPriceInput.value);
      const tradePriceForce = parseFloat(this.sellPriceInput.value); // trade == sell

      if (!(isNaN(netPriceForce) || isNaN(listPriceForce) || isNaN(tradePriceForce)) && tradePriceForce !== 0) {
        this.grossProfitPercentage = Math.trunc(((tradePriceForce - netPriceForce) / tradePriceForce) * 100).toFixed(0);
      } else {
        this.grossProfitPercentage = '';
      }
    } else {
      this.applyButtonDisabled = true;
      this.grossProfitPercentage = '';
    }
  }

  render() {
    // Popover to explain multi-use list input (dollar or % markup, analogous to PIN1)
    const listPricePopover = (
      <Popover id="modal-popover">
        <Popover.Title>% MARKUP HELP</Popover.Title>
        <Popover.Content>
          % of markup will apply to <strong>SELL</strong> price.
          <br />
          2*% of markup will apply to <strong>LIST</strong> price.
          <br />
          <em>Leave blank to use pricing escalators.</em>
        </Popover.Content>
      </Popover>
    );

    const unpricedPartPopover = (
      <Popover id="modal-popover">
        <Popover.Title>UNPRICED HELP</Popover.Title>
        <Popover.Content>
          If a default part is issued unpriced, the repair order will be unable to be closed until an adjustment is made
          and a price is set for the part.
        </Popover.Content>
      </Popover>
    );

    return (
      <Modal
        backdrop="static"
        container={this.props.container as any}
        dialogClassName="sk-defaultPartModal"
        onHide={() => this.props.hide()}
        show={this.props.show}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>{this.defaultPart.desc}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!this.subletDefaultPart && (
            <div>
              <Label>DESCRIPTION</Label>
              <input
                defaultValue={this.props.data.description ? this.props.data.description : this.defaultPart.desc}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    if (this.subletDefaultPart) {
                      this.subletCommentInput.focus();
                    } else {
                      this.netPriceInput.focus();
                    }
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.descriptionInput = ref)}
                type="text"
                style={{ width: '100%' }}
              />
            </div>
          )}
          {this.subletDefaultPart && (
            <div>
              <Label>SUBLET COMMENT</Label>
              <input
                defaultValue={this.props.data.subletComment}
                onBlur={evt => this.setSubletComment(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.setSubletComment(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.subletCommentInput = ref)}
                type="text"
                style={{ width: '100%' }}
              />
              {this.showSubletCommentRequired && <div style={{ color: 'red' }}>Sublet Comment Required</div>}
              <input
                defaultValue={this.defaultPart.desc}
                ref={(ref: HTMLInputElement) => (this.descriptionInput = ref)}
                type="hidden"
              />
            </div>
          )}
          <hr style={{ marginBottom: '15px', marginTop: '20px' }} />
          {this.defaultPart.reverseSign && (
            <div style={{ marginBottom: '7px' }}>The amount specified will be SUBTRACTED from the invoice.</div>
          )}
          <div className="sk-pricingControls">
            <div>
              <Label>NET</Label>
              <input
                disabled={this.unpriced}
                defaultValue={
                  this.props.showDefaultNetPrice ? this.defaultNetPrice : this.positiveString(this.props.data.netPrice)
                }
                onBlur={() => this.overrideNet(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideNet(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.netPriceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{this.positiveString(this.props.data.netPrice)}</div>
            </div>
            <div>
              <Label>% MARKUP</Label>
              <input
                disabled={_.includes(['1', '2'], this.defaultPart.arity) || this.unpriced}
                onBlur={() => this.overrideMarkup(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideMarkup(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.markupOverrideInput = ref)}
                type="text"
              />
              <br />
              <OverlayTrigger overlay={listPricePopover}>
                <div className="sk-listPriceOverrideHelp">
                  <i className="zmdi zmdi-help" />
                </div>
              </OverlayTrigger>
            </div>
            <div>
              <Label>LIST</Label>
              <input
                defaultValue={this.positiveString(this.props.data.listPrice)}
                disabled={_.includes(['1', '2'], this.defaultPart.arity) || this.unpriced}
                onBlur={() => this.overrideList(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideList(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.listPriceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{this.positiveString(this.props.data.listPrice)}</div>
            </div>
            <div>
              <Label>SELL</Label>
              <input
                defaultValue={this.positiveString(this.props.data.tradePrice)}
                disabled={this.defaultPart.arity === '1' || this.unpriced}
                onBlur={() => this.overrideSell(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideSell(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.sellPriceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{this.positiveString(this.props.data.tradePrice)}</div>
            </div>
          </div>
          {this.model.sysParams.displayGrossProfitOnInquiry && this.grossProfitPercentage !== '' && !this.unpriced && (
            <div className="sk-grossProfitPercentage">Gross Profit: {this.grossProfitPercentage}%</div>
          )}
          {this.props.unpricedOptionAvailable && (
            <>
              <hr style={{ marginBottom: '15px', marginTop: '20px' }} />
              <div>
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    defaultChecked={this.props.unpricedOptionChecked}
                    onChange={(evt: any) => {
                      this.unpriced = evt.target.checked;
                      this.setDefaultPartModalState();
                    }}
                    id="unpriced"
                    className="sk-useMaterialInput ml-1"
                    tabIndex={-1}
                  />
                  <FormCheck.Label htmlFor="unpriced">
                    Unpriced Part
                    <OverlayTrigger overlay={unpricedPartPopover}>
                      <span style={{ marginLeft: '15px' }}>
                        <i className="zmdi zmdi-help" />
                      </span>
                    </OverlayTrigger>
                  </FormCheck.Label>
                </FormCheck>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => this.props.hide()} variant="secondary">
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={this.applyButtonDisabled}
            onClick={() => this.apply()}
            ref={(ref: HTMLButtonElement) => (this.applyButtonRef = ref)}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default DefaultPartModal;
